import { isFilledObject } from '$lib/components/utils/checker';
import { onNextIdle } from '$lib/services/gtm/functions';

import type { DataLayer } from '@api/types';
import { getXhData } from '@api/utils';
import { Cookie } from '@shared/cookies';
import { formatXhExperiments } from '@shared/utils/xhData';
import { getCookie, setCookie } from '@utils/cookies';

import { browser } from '$app/environment';

let gtmPromise: Promise<unknown> | null = null;
let initials: DataLayer | null = null;

const addData = async (data: object, dataLayer = window.dataLayer) => {
    if (!isFilledObject(data)) {
        console.error('data isn\'t valid');
        return undefined;
    }

    if (!browser) {
        return undefined;
    }
    try {
        if (gtmPromise) {
            await gtmPromise;
        }
        const cookieChannel = getCookie(Cookie.AttributionChannel);
        const channel = initials?.attribution_channel;
        if (channel && channel !== cookieChannel) {
            setCookie(Cookie.AttributionChannel, channel);
        }
        const xhExperiments = formatXhExperiments(
            getXhData()?.experiments ?? ''
        )?.map(ex => `${ex.type}_${ex.v}_${ex.g?.toUpperCase() ?? ''}`);

        return dataLayer.push({
            ...data,
            ...(channel ? { attribution_channel: channel } : {}),
            ...(xhExperiments ? { xh_experiments: xhExperiments } : {}),
        });
    } catch (e) {
        console.error(e);
    }
};

const initGTM = (gtmId: string) => {
    return new Promise((resolve, reject) => {
        window.dataLayer = window.dataLayer || [];

        const script = document.createElement('script');
        script.async = true;
        script.defer = true;
        script.src = `https://www.googletagmanager.com/gtm.js?id=${gtmId}`;
        script.setAttribute('nonce', 'flirtify');

        script.onload = () => {
            const nativePush = window.dataLayer.push;
            // @ts-expect-error - window types
            window.dataLayer.push = async (...args) => {
                await onNextIdle();
                return nativePush(...args);
            };
            resolve(window.google_tag_manager?.[gtmId]);
        };

        script.onerror = (error) => {
            console.error('Failed to load GTM script', error);
            reject(error);
        };

        document.head.prepend(script);
    });
};

export const gtmService = () => {
    const init = async (gtmId: string, initial: object | null) => {
        initials = initial as DataLayer;
        if (!gtmPromise && browser) {
            gtmPromise = initGTM(gtmId);

            if (initial) {
                await addData(initial);
            }

            await addData({
                event: 'gtm.js',
                'gtm.start': new Date().getTime(),
            });
        }
    };

    return {
        init,
        addData,
    };
};
